import React from 'react';
import { useAppSelector, useHistory } from 'src/utils/Hooks';
import EntityCard, { EntityCardProps } from '../EntityCard/EntityCard';
import { getProductImageUrl } from 'src/utils/image';
import colorMap from 'src/utils/colors';
import ElementCard from '../ElementCard/ElementCard';
import { BrandLogo } from 'src/components/BeaconV2Components/Generic/BrandLogo';
import EntityProductCard from './EntityProductCard';
import {
  BEACON_PRO_SIDEBAR_IMAGE_MAX_HEIGHT,
  BEACON_PRO_SIDEBAR_IMAGE_WIDTH
} from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import EntityPillDropdown from 'src/components/BeaconV2Components/EntityPillDropdown';
import useCreateSegmentLink from 'src/utils/Hooks/useCreateSegmentLink';
import { Segment } from 'sl-api-connector';
import _get from 'lodash/get';
import { useStacklineTheme } from '@stackline/ui';
import { SxProps } from '@mui/system';
import { ProductImage } from 'src/components/BeaconV2Components/ProductCard';
import { Topic, TopicGroup } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/types';
import { getPeriodicLabel } from 'src/utils/stringFormatting';
import { PILL_TYPE } from 'src/components/BeaconV2Components/GenericSidebarNav/constants';
import { useCategoryEntity, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import {
  StorageSource,
  useTopicBuilderActions
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import { useAddTopicGroupStoreActions } from 'src/zustandStores/BeaconPro/Topics/TopicGroups/useAddTopicGroupStore';
import { getTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { ClientLogo } from 'src/components/BeaconV2Components/Generic/ClientLogo';
import { getAppName, getQueryParamValue } from 'src/utils/app';
import { BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import colors from 'src/utils/colors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useMicroTrendSaveAsSegmentStore } from 'src/components/AtlasV2Components/AtlasKeyMetricsV2PageComponents/MicroTrend/MicroTrendStore';
import { useEntityMetadataEnhancer } from 'src/components/AtlasV2Components/Hooks/useEntityMetadataEnhancer';

const periodicCardContainerProps: SxProps = {
  height: '72px'
};

export const EntityCardContainer = () => {
  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);
  const appName = getAppName();
  const subTab = getQueryParamValue('subtab');
  const entityType = useMainEntityType();

  const { additionalParams, searchParams } = useAppSelector((state) => state.app.queryParams);
  const createSegmentLink = useCreateSegmentLink();
  const savedSearchesById = useAppSelector((state) => state.segments.savedSearchesById);
  const history = useHistory();
  const userId = useAppSelector((state) => state.user.session.userId);
  const theme = useStacklineTheme();

  const topicClassification = getTopicOrTopicGroupType(mainEntity?.sharedWithEntityId, mainEntity?.ownedByUserId);
  const isOwned = topicClassification === 'Owned';
  const { storage } = useTopicBuilderActions();
  const { openModal: openAddTopicGroupModal, setTopicGroupState } = useAddTopicGroupStoreActions();
  const { isAllCategoriesEntity } = useCategoryEntity();
  const { getEntityDisplayName } = useEntityMetadataEnhancer();
  const { saveAsSegment } = useMicroTrendSaveAsSegmentStore();
  const retailer = useAppSelector((state) => state.retailer);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);

  const getSegmentTopicDropdownOptions = (entityId: string) => {
    return [
      ...(_get(savedSearchesById ? savedSearchesById.get(entityId) : null, 'userId') === userId
        ? [
            {
              id: 'edit',
              label: 'Edit'
            }
          ]
        : []),
      {
        id: 'duplicate',
        label: 'Duplicate'
      }
    ];
  };

  /**
   * Case 1: If user is already in Topic -> Settings, do not show Edit option
   * Case 2: If user is in Topic -> Summary, show Edit option
   * Future case 1: only show Duplicate option if user is the owner of the topic
   * Future case 2: only show Duplicate option if user is not on Topic -> Settings, as there is another button for it
   * @returns Dropdown options for new topic cards
   */
  const getNewTopicDropdownOptions = () => {
    const pillDropdownOptions = [];
    if (!(appName === 'beacon' && entityType === 'topic' && subTab === BEACON_SUBTABS.SETTINGS) && isOwned) {
      pillDropdownOptions.push({ id: 'edit', label: 'Edit' });
    }

    pillDropdownOptions.push({
      id: 'duplicate',
      label: 'Duplicate'
    });
    return pillDropdownOptions;
  };

  /**
   * Handles the Save as Segment action for microtrends
   */
  const handleMicroTrendSaveAsSegment = async () => {
    const url = await saveAsSegment({ mainEntity, retailer, mainTimePeriod, getEntityDisplayName });
    if (url) {
      history.push(url);
    }
  };

  const entityTypeToCardProps: { [key: string]: EntityCardProps } = {
    company: {
      title: mainEntity.name,
      header: <ClientLogo displayName={mainEntity.name} />,
      pill: PILL_TYPE.ORGANIZATION
    },
    client: {
      title: mainEntity.name,
      header: <ClientLogo displayName={mainEntity.name} />,
      pill: PILL_TYPE.ORGANIZATION
    },
    brand: {
      title: mainEntity.name,
      header: (
        <BrandLogo
          brandId={mainEntity.brandId}
          displayName={mainEntity.name}
          elementCardVariant="large"
          height={80}
          width={80}
        />
      ),
      pill: PILL_TYPE.BRAND
    },
    category: {
      title: mainEntity.name,
      header: (
        <ElementCard
          color={isAllCategoriesEntity ? 'graysPrimary' : 'accentTeal'}
          textSx={isAllCategoriesEntity ? { color: colors.darkBlue } : {}}
          isAllCategoriesEntity={isAllCategoriesEntity}
        >
          {getPeriodicLabel(mainEntity.name)}
        </ElementCard>
      ),
      pill: isAllCategoriesEntity ? (
        <div
          style={{
            backgroundColor: theme.colors.secondaryPorcelain,
            borderRadius: '12px',
            padding: '4px 24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '103px'
          }}
        >
          <Text variant="subtitle3" sx={{ lineHeight: '16px' }}>
            Collections
          </Text>
        </div>
      ) : (
        'Category'
      ),
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    segment: {
      title: mainEntity.name,
      header: <ElementCard color="info">{getPeriodicLabel(mainEntity.name)}</ElementCard>,
      pill: (
        <EntityPillDropdown
          slMenuProps={{
            options: getSegmentTopicDropdownOptions(mainEntity.id as string),
            onChange: (option) =>
              history.push(
                createSegmentLink(savedSearchesById.get(mainEntity.id as string) as Segment, option.id === 'edit')
              )
          }}
          pillStyles={{ width: '124px' }}
        >
          Segment
        </EntityPillDropdown>
      ),
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    // Legacy topic card
    searchtermlist: {
      title: mainEntity.name,
      header: <ElementCard color="accentTangerine">{getPeriodicLabel(mainEntity.name)}</ElementCard>,
      pill: (
        <EntityPillDropdown
          slMenuProps={{
            options: getSegmentTopicDropdownOptions(mainEntity.id as string),
            onChange: (option) =>
              history.push(
                createSegmentLink(savedSearchesById.get(mainEntity.id as string) as Segment, option.id === 'edit')
              )
          }}
        >
          Topic
        </EntityPillDropdown>
      ),
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    }, // New topic card
    topic: {
      title: mainEntity.name,
      header: <ElementCard color="accentTangerine">{getPeriodicLabel(mainEntity.name)}</ElementCard>,
      pill:
        (mainEntity as unknown as Topic).sharedWithEntityId === null ? (
          'Standard Topic'
        ) : (
          <EntityPillDropdown
            slMenuProps={{
              options: getNewTopicDropdownOptions(),
              onChange: (option) => {
                switch (option.id) {
                  case 'edit':
                    history.push(`/topic/${mainEntity.id}${searchParams}&tab=traffic&subtab=settings`);
                    break;
                  case 'duplicate': {
                    // Save the topic to local storage so that it can be retrieved when the user navigates to the topic builder
                    storage.save(mainEntity as Topic, StorageSource.DUPLICATE);

                    history.push(`/search${searchParams}&entityType=searchtermlist&action=create`);
                    break;
                  }
                  default:
                }
              }
            }}
          >
            Custom Topic
          </EntityPillDropdown>
        ),
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    topicgroup: {
      title: mainEntity.name,
      header: <ElementCard color="accentPlum">{getPeriodicLabel(mainEntity.name)}</ElementCard>,
      pill:
        (mainEntity as unknown as TopicGroup).sharedWithEntityId === null ? (
          'Standard Topic Group'
        ) : (
          <EntityPillDropdown
            slMenuProps={{
              options: [
                ...(isOwned ? [{ id: 'edit', label: 'Edit' }] : []),
                {
                  id: 'duplicate',
                  label: 'Duplicate'
                }
              ],
              onChange: (option) => {
                switch (option.id) {
                  case 'edit':
                    history.push(`/topicgroup/${mainEntity.id}${searchParams}&tab=traffic&subtab=settings`);
                    break;
                  case 'duplicate': {
                    setTopicGroupState(({ setCategoryIds }) => setCategoryIds(mainEntity?.includedCategoryIds ?? []));
                    setTopicGroupState(({ setSubcategoryIds }) =>
                      setSubcategoryIds(mainEntity?.includedSubCategoryIds ?? [])
                    );
                    setTopicGroupState(({ setTopicIds }) => setTopicIds(mainEntity?.topicIds ?? []));
                    openAddTopicGroupModal();
                    break;
                  }
                  default:
                }
              }
            }}
          >
            Custom Topic Group
          </EntityPillDropdown>
        ),
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    subcategory: {
      title: mainEntity.name,
      header: <ElementCard color="accentMagenta">{getPeriodicLabel(mainEntity.name)}</ElementCard>,
      pill: PILL_TYPE.SUBCATEGORY,
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    product: {
      title: mainEntity.brandName,
      header: (
        <ProductImage
          imageUri={getProductImageUrl(mainEntity.stacklineSku)}
          mainDisplayTitle={mainEntity.name}
          secondaryDisplayTitle={mainEntity.shortDisplayName}
          width={BEACON_PRO_SIDEBAR_IMAGE_WIDTH}
          height={BEACON_PRO_SIDEBAR_IMAGE_MAX_HEIGHT}
          fallbackImageStyle={{ marginBottom: 0 }}
        />
      ),
      subtitleMarginTop: '8px',
      subtitleText: mainEntity.name,
      titleLink: `/brand/${mainEntity.brandId}${searchParams}${additionalParams}`
    },
    businessunit: {
      title: mainEntity.name,
      header: <ElementCard color="success">{getPeriodicLabel(mainEntity.name)}</ElementCard>,
      pill: PILL_TYPE.BUSINESS_UNIT,
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    adEntity: {
      title: mainEntity.displayName,
      header: <ElementCard color="accentSand">{getPeriodicLabel(mainEntity?.displayName || '')}</ElementCard>,
      pill: PILL_TYPE.AD_ENTITY,
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    adPortfolio: {
      title: mainEntity.displayName,
      header: (
        <ElementCard
          color="accentBlue"
          textSx={{
            color: colorMap.darkBlue
          }}
        >
          {getPeriodicLabel(mainEntity?.displayName || '')}
        </ElementCard>
      ),
      pill: PILL_TYPE.AD_PORTFOLIO,
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    adGroup: {
      title: mainEntity.displayName,
      header: <ElementCard color="accentMagenta">{getPeriodicLabel(mainEntity?.displayName || '')}</ElementCard>,
      pill: PILL_TYPE.AD_GROUP,
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    adCampaign: {
      title: mainEntity.displayName,
      header: (
        <ElementCard
          color="accentMagenta"
          textSx={{
            color: colorMap.darkBlue
          }}
        >
          {getPeriodicLabel(mainEntity?.displayName || '')}
        </ElementCard>
      ),
      pill: PILL_TYPE.AD_CAMPAIGN,
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    adTarget: {
      title: mainEntity.displayName,
      header: (
        <ElementCard
          color="accentMagenta"
          textSx={{
            color: colorMap.darkBlue
          }}
        >
          {getPeriodicLabel(mainEntity?.displayName || '')}
        </ElementCard>
      ),
      pill: PILL_TYPE.AD_TARGET,
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    },
    microtrend: {
      title: mainEntity.displayName,
      header: (
        <ElementCard color="brickOrange" textSx={{ color: '#BC3C19' }}>
          {getPeriodicLabel(mainEntity?.displayName || '')}
        </ElementCard>
      ),
      pill: (
        <EntityPillDropdown
          slMenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center'
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center'
            },
            sx: {
              '& .MuiMenuItem-root': {
                width: '168px',
                justifyContent: 'center',
                textAlign: 'center'
              }
            },
            options: [
              {
                id: 'saveAsSegment',
                label: 'Save as segment'
              }
            ],
            onChange: (option) => {
              if (option.id === 'saveAsSegment') {
                handleMicroTrendSaveAsSegment();
              }
            }
          }}
          pillStyles={{ width: '144px' }}
          iconStyle={{ right: '8px' }}
        >
          Microtrend
        </EntityPillDropdown>
      ),
      titleMarginTop: theme.spacing.md,
      headerContainerSx: periodicCardContainerProps
    }
  };

  return mainEntity.type === 'product' ? (
    <EntityProductCard />
  ) : entityTypeToCardProps[mainEntity.type] ? (
    <EntityCard {...entityTypeToCardProps[mainEntity.type]} />
  ) : (
    <div style={{ width: '140px', height: '175px' }}></div>
  );
};
